// "Building Precise Passers: A Deep Dive" — the masterclass about where the ball
// arrives, and what it costs the receiver when it arrives somewhere else.
//
// The copy is Coleman's, from his Word pass of 2026-10-05, applied verbatim.
// Nothing here is written by the assistant: when a line needed to change, it
// changed in his document first. The captions on the clips are the "Text:"
// notes he attached to them in that pass.
//
// RESEARCH NOTE, because the sourcing is the part worth protecting:
//
//   Quílez Maimón et al. 2020 supports uncertainty, variable conditions,
//   pressure and small sided games. Its fatigue conclusion is about PHYSICAL
//   conditioning, so it is not the source for anything about mental fatigue.
//
//   Mental fatigue leads on Zhou et al. 2026, which measured passing accuracy
//   and pass placement directly, with Cao et al. 2022 underneath it as a
//   corroborating review. Collapsing the three would put a finding onto a
//   paper that does not make it.
//
//   The 10%+ in-zone shooting claim IS in the copy now, in chapter 1, hedged
//   in Coleman's own words as unverified and anecdotal, and the research note
//   still carries the fuller caveat. Both were his call. Do not harden the
//   hedge into a fact, and do not quietly drop the claim again.
//
// Layout is "article": see ArticleBlocks.jsx. No em dashes.

export const PASSING_ACCURACY_MODULE_KEY = "passing-accuracy";

// Drill footage, by where it is used. Titles are Coleman's.
//
// A null is a clip named in the copy with no playback id yet. The VideoBlock
// renders its coming-soon placeholder, so the page still ships.
//
// These are PLAYBACK ids, which is the thing that went wrong on 2026-10-05.
// Seven clips here are masterclass-only uploads, not drills, so their ids
// could not come from the library like the rest. They were read instead off
// the Mux Assets LIST, whose prominent string per row is the ASSET id, and
// an asset id is a different value from the playback id for the same video
// (in the drills table, "1v2 snake and jail" is asset rtYd202r6rDL... and
// playback CZgsoCM02cZQ...). Same shape, so it looked right. Every one of
// the seven threw "Network Error: the video URL or playback-token are
// formatted with incorrect or incomplete information" in production.
//
// The playback id lives on the asset's own page, under Playback and
// Thumbnails. Asset to playback, for anyone retracing this:
//
//   bB00T1qDvbuv3jVdWjdiEaVG8LPSqAeSq35x4FOg3XDo  Release Points
//   ZvolCPkY5YEMJN7MVnkxFTQtTBB026DnEPexFqhLHYTs  Longer Passes
//   x5JIsNmdClvvz8ymDqg5UljjswOCXeDvk1YB5tIeWno   Adding a Passer to Games
//   GcyuK9XorV00rXI5yhBGKvZPsCFi1hY02vtCBHwNF9xVY One Hand Overload
//   NKBMYIdclRfXPGPQXPv4SmvB3Wi6ZSBsJ5Y004Wyrs4Q  360 Passing
//
// The rule, so this does not repeat: an id for this file comes from the
// drills table, from the Playback and Thumbnails tab, or from Coleman as
// text. Never from the Assets list, and never read off a screenshot when
// it can be copied.
//
// All 22 play. The last two took a second round for the same underlying
// reason: the ids were the right KIND, playback not asset, but still read
// off a screenshot, and both were wrong by exactly one character, an
// uppercase I read as a lowercase l. Those are the same glyph in Mux's
// typeface, so there is no amount of care that makes reading an id off an
// image safe. Copy it.
const VIDEOS = {
  releasePoints: "KPl31LsHes01g5o01hehQtWxsBC9EsDMESSHYL9EEkEmg",    // Accuracy Passing Release Points
  contestedLead: "3yyJ3rcF92g2Q01TzgDHc026eZleC7i33dIOEL5Uq01B94",   // Contested Lead Pass
  longerPasses: "bSzyJXjD4jzfnJ00jo5wlW501W8EM8txdNEy9TcvBEjak",     // Longer Passes for Younger Players
  transitionDime: "3oM7SIAHzSe6OzoDeTPoQa3QbuX1iTb6DuTkAGI21ys",     // Transition Dime Passing
  triangle5: "iQgmODTF3o1SkUZQ8yvfBSrmrLFTRd7DewIICK01FU9o",         // 3v2 TRIANGLE 5 PASSES
  trapBallScreen: "aM2TY5ZyMOfSjAyjTh01p7xeiHO5dfBloC9E7O7a83EY",    // Trap Ball Screen 3v3
  pgControlling2v1: "xLy5xBLHDgBhVi213qyhidioYFUWVj3bSBYg5SuHddw",   // PG Skills: Controlling the 2v1
  feetAnchored: "CavpJKiVtwcfXLEaRNuiXNyVhogACJpEjM7Pw35QGzI",       // Feet Anchored Switch Hands to Pass
  basicOneHand: "jEsOvljzpZZVLgW00FP8SpF7mVq6lK1KiMDyrda25HAg",      // Basic One Hand Passing Warmup
  passingWarmup: "Lqa017O8qTc2kIMraBnCLm00OvCVVJo7cfRSH3ggscLFo",    // Passing Warmup
  simultaneous: "WbpckQm00d7iUEfCix5Bk00fcrLLqHPyEWcXSOednY01lw",    // Simultaneous Passing W Defense
  throughPressure: "00kVvEdp4z5KnEiJTtE01r602ZoIweCntohj29PBWQYicc", // Passing Through Pressure
  addingPasser: "wwYlQaPxZjoeT0102k16T01NzSCp00xTck16xd027vd2HBmk",  // Adding a Passer to Games
  gapHelpRead: "z502CAQjKpCJ302vN9nWsaj5G1IHiKGDVsqmh9Tzpfaqg",      // Gap Help Read 2v1 (library title)
  contestedCatches: "6jBs2PsozQd3j44OTTIoYlprRrubx00LFNXFed00hJwl00",// Contested Catches
  dexterity: "GPrXMqHM9fFiwKbpqk77G023yA00DKh01PgcghhhNPSquM",       // Passing & Handling Dexterity Warmup Series
  oneHandOverload: "YBLeZke2r00X3IvAmMspwFwfrBvL00r01bcaueTdU4mMnc", // One Hand Overload
  threeSixty: "PGIKFsXnI00oncbxKYFq8MUEir7iaD2mh02xz561UKOfs",       // 360 Passing
  strikeZoneShoot: "tAzIom02pUJ6ZpwTGiYqoeNn7bW02008GF5yZTPwAeSohM", // Strike Zone Dynamic Shooting
  pushPull: "wLHjR9n1QaU01hnrnWDV01aY3x02sOY1MYk201hDKn5SkfY",       // 2v2 Push Pull
  bounceConstraint: "cyZIZSpuWge02K02crgQNlNyPnQ2DlrNyQ4LKbXwtbmpI", // Bounce Pass Constraint
  pgKeys: "02pU5cn4NeA46DLM1vnIkXO7DQ7jQk2ujESjAWxEjg5s",            // PG Keys: Passing Development
};

export const PASSING_ACCURACY_MODULE = {
  key: PASSING_ACCURACY_MODULE_KEY,
  title: "Building Precise Passers: A Deep Dive",
  sub:
    "Considering how much each pass affects what happens next, it’s worth spending time understanding how to get your players throwing “strikes.”",
  duration: "~30 min read · 22 clips",
  // Gives the shelf card a real frame instead of the shared mc-art SVG
  // (see masterclass/coverArt.js).
  coverVideoId: VIDEOS.simultaneous,
  // Reads as a long-form piece rather than a stack of cards. Per module
  // on purpose, so the other twelve are untouched.
  layout: "article",
  chapters: [
    // =================================================================
    {
      id: "why-it-matters",
      title: "Why strike zone passes matter",
      section: "Why strike zone passes matter",
      divider: true,
      blocks: [
      ],
    },
    // =================================================================
    {
      id: "pass-is-part-of-the-shot",
      title: "The pass is part of the shot",
      section: "Why strike zone passes matter",
      blocks: [
        { kind: "text", md:
          "Passing accuracy isn’t just about avoiding turnovers. Where the ball arrives completely changes what the receiver is able to do next." },
        { kind: "text", md:
          "A pass that arrives cleanly in a shooter's “strike zone” lets them flow directly into their shot, an extra pass, or even a closeout, whereas a pass that arrives high, low, behind them, outside their frame, or at the wrong speed forces them to solve another problem before they can do anything." },
        { kind: "text", md:
          "The process may look like this:" },
        { kind: "principles", items: [
          { n: "1", title: "Reach for the ball", body: "The catch happens outside their frame, so the first thing they do is bring it back in." },
          { n: "2", title: "Reorganize their feet", body: "The arrival pulls them off their base and the feet have to be reset before anything else can happen." },
          { n: "3", title: "Move the ball to the pocket", body: "The ball is caught somewhere other than where the shot starts, so there is a step to center the ball." },
          { n: "4", title: "Change their timing", body: "The rhythm they were about to shoot on is gone and a new one has to be found." },
        ]},
        { kind: "heading", level: 3, text: "Strike-zone passing" },
        { kind: "text", md:
          "Think of every receiver as having a strike zone. Where it is changes depending on what they are doing, where they are moving, what action is coming next, which hand or release they need, and what the defense is doing. There is no single universal target, which is part of why this is such an art!" },
        { kind: "accuracyGraphic", name: "strike-zone", caption:
          "Every amber pass still gets caught. Each one buys the receiver a problem to solve before they can shoot, and the defense gets that time back." },
        { kind: "text", md:
          "Some coaches have even claimed differences of 10%+ in shooting percentage depending on whether the pass arrives in or outside the shooter's strike zone, which we have not been able to verify, but are worth noting anecdotally and goes to show:" },
        { kind: "text", md:
          "We can improve players’ shooting without ever even working on the shot." },
      ],
    },
    // =================================================================
    {
      id: "more-than-a-target",
      title: "Accuracy is more than hitting a target",
      section: "Why strike zone passes matter",
      blocks: [
        { kind: "text", md:
          "If passing accuracy were simply the ability to throw the ball to the same stationary target every time, it would be pretty damn easy to train. But basketball doesn’t happen that way. So it’s worth asking the question: why do passes end up being inaccurate in the first place? This helps us organize our thoughts around what we should include in training to prepare for game-like conditions." },
      ],
    },
    // =================================================================
    {
      id: "what-causes-bad-passes",
      title: "What causes bad passes",
      section: "What causes bad passes",
      divider: true,
      blocks: [
        { kind: "text", md:
          "What variables turn strikes into turnovers?" },
      ],
    },
    // =================================================================
    {
      id: "factor-1-defender-on-passer",
      title: "Factor 1: the defender on the passer",
      section: "What causes bad passes",
      blocks: [
        { kind: "text", md:
          "The first defender changes the available release window. Even if the receiver never moves, the passer may no longer be able to deliver the ball from their preferred angle." },
        { kind: "text", md:
          "The defender can force the passer to raise the release, widen it, use a different hand, go around, go over, go underneath, or change the timing of the pass entirely. The passer cannot rely on simply reproducing the same movement every time. They have to find a solution that gets the ball through the window that exists while still arriving accurately." },
        { kind: "accuracyGraphic", name: "release-windows", caption:
          "The receiver has not moved and the defender can only cover one window at a time. Each remaining release reaches the same place by a different path, which is why the trajectory is the variable and the destination is not." },
        { kind: "video", muxPlaybackId: VIDEOS.releasePoints,
          name: "Accuracy Passing Release Points",
          caption: "Preparing players to identify the passing window and develop the “degrees of freedom” to make those passes in a split second is a huge focus of our passing work." },
        { kind: "callout", tone: "tip", title: "The question to ask",
          body: "Can the player hold the same target accuracy when their preferred release point disappears? That is a different question from whether they can make the pass at all, and it is the one worth watching for." },
      ],
    },
    // =================================================================
    {
      id: "factor-2-defender-on-receiver",
      title: "Factor 2: the defender on the receiver",
      section: "What causes bad passes",
      blocks: [
        { kind: "text", md:
          "Now the problem changes. The passer may have a clear release window, but the ball still has to arrive safely around the receiver's defender. They now have to account for ball speed, trajectory, the defender's position, the receiver's movement, and the space available." },
        { kind: "text", md:
          "Sometimes a direct pass works. Sometimes the ball needs more speed. Sometimes it needs to be lofted. Sometimes a bounce pass is the better answer. Sometimes the ball has to lead the receiver away from the defender." },
        { kind: "text", md:
          "The target stops being \"hit the player\" and becomes \"put the ball where only the offense can get it... but also where they can do something with it\"" },
        { kind: "video", muxPlaybackId: VIDEOS.contestedLead,
          caption: "Now, the initial defender isn’t the constraint. It’s the defender chasing down the lead pass. Finding a window that gets it around the defender but to the offense in a good place to score is an art that requires repetition without repetition." },
      ],
    },
    // =================================================================
    {
      id: "factor-3-moving-receiver",
      title: "Factor 3: a moving receiver",
      section: "What causes bad passes",
      blocks: [
        { kind: "text", md:
          "A moving receiver changes the problem immediately. The passer now has to understand the relationship between receiver speed, ball speed, trajectory, angle and timing. They are not throwing to where the player is. They are throwing to where the player is going to be." },
        { kind: "text", md:
          "Even small movements change the target. This matters most in offenses where players are constantly making small spacing adjustments. The receiver may only move a few feet, and that can completely change the timing and location the pass requires." },
        { kind: "accuracyGraphic", name: "moving-target", caption:
          "The ball is not thrown to where the receiver is. It is thrown to the spot they have not reached yet, which means the passer is aiming at a moment as much as at a place." },
        { kind: "text", md:
          "The target in basketball is rarely a point. It is usually a moving window." },
        { kind: "text", md:
          "Even if the passer is stationary and the defender far enough behind to not pose a challenge by default, distance passes can really overload the accuracy system here by challenging their understanding of the speed, angle, and timing relationship." },
        { kind: "video", muxPlaybackId: VIDEOS.longerPasses,
          caption: "Longer Passes for Younger Players" },
        { kind: "video", muxPlaybackId: VIDEOS.transitionDime,
          caption: "Transition Dime Passing" },
      ],
    },
    // =================================================================
    {
      id: "factor-4-moving-passer",
      title: "Factor 4: a moving passer",
      section: "What causes bad passes",
      blocks: [
        { kind: "text", md:
          "When moving, the passer now has their own momentum to account for: body position, balance, release angle, which hands are available, speed and direction, all changing while the pass is being made. Plus, more tension throughout the body which will vary on essentially every single pass." },
        { kind: "text", md:
          "The same target can require a completely different solution depending on what the passer's body is doing. Sometimes they can square up and make a traditional pass. Sometimes they cannot, and they need a one-hand push, a hook pass, a pass while moving the other way, a release from outside their normal frame, or a pass immediately off the pickup." },
        { kind: "video", muxPlaybackId: VIDEOS.triangle5,
          caption: "3v2 TRIANGLE 5 PASSES" },
        { kind: "text", md:
          "Find ways to get passers moving on their passes…" },
      ],
    },
    // =================================================================
    {
      id: "factor-5-unpredictability",
      title: "Factor 5: unpredictability",
      section: "What causes bad passes",
      blocks: [
        { kind: "text", md:
          "This may be the biggest single difference between a drill and a game. In most passing drills the player already knows who they are passing to, when, what type of pass it will be, where the receiver will be, and where the defender will be. The motor system has plenty of time to prepare." },
        { kind: "text", md:
          "Games are different. A player probes the defense and the window appears for a moment." },
        { kind: "text", md:
          "The time between seeing the opportunity and executing the pass can be extremely small, and in that time they may have to adjust release point, angle, timing, velocity and the type of pass." },
        { kind: "heading", level: 3, text: "Seeing the game earlier makes the pass easier" },
        { kind: "text", md:
          "Greats passers aren’t just better at physically passing the ball. Some of them make the physical problem easier because they see the game sooner." },
        { kind: "text", md:
          "If a player recognizes a developing window half a second earlier, they have more time to organize their body and prepare a clean pass. Instead of reacting to the defense at the last possible moment, they are anticipating what is likely to happen next. That extra time improves body organization, release selection, timing and accuracy." },
        { kind: "text", md:
          "Sometimes the best way to improve passing accuracy is not to work on the hands at all. It is to give the player more time by helping them read the game earlier." },
        { kind: "video", muxPlaybackId: VIDEOS.trapBallScreen,
          caption: "Trap Ball Screen 3v3" },
        { kind: "text", md:
          "this is why incentivizing quality passes through fast-moving live play is the gold standard here, which we’ll discuss." },
      ],
    },
    // =================================================================
    {
      id: "factor-6-pickup",
      title: "Factor 6: the pickup",
      section: "What causes bad passes",
      blocks: [
        { kind: "text", md:
          "The pass often begins before the ball ever leaves the player's hand. How they pick the ball up determines which passing solutions are even available to them." },
        { kind: "text", md:
          "Players need enough ball control to organize the ball out of a high dribble, a low dribble, a fast one, a slow one, with the ball outside their frame, and from awkward body positions. If a player needs a perfect pickup before they can make an accurate pass, their passing options are extremely limited. The cleaner and more adaptable the pickup, the more solutions they can reach." },
        { kind: "video", muxPlaybackId: VIDEOS.feetAnchored,
          caption: "Feet Anchored Switch Hands to Pass" },
      ],
    },
    // =================================================================
    {
      id: "factor-7-eyes",
      title: "Factor 7: looking somewhere else",
      section: "What causes bad passes",
      blocks: [
        { kind: "text", md:
          "Many times, the passer cannot stare directly at the target they intend to hit. Players need to be able to perceive movement without giving the pass away too early." },
        { kind: "text", md:
          "That can mean looking off a defender, briefly locating the receiver and then moving the eyes elsewhere, using peripheral vision, or tracking a moving receiver without staring at them. The player is no longer looking at a target and throwing accurately. They are gathering information from several places at once and still delivering the ball precisely." },
        { kind: "video", muxPlaybackId: VIDEOS.pgControlling2v1,
          caption: "PG Skills: Controlling the 2v1" },
      ],
    },
    // =================================================================
    {
      id: "factor-8-fatigue",
      title: "Factor 8: fatigue",
      section: "What causes bad passes",
      blocks: [
        { kind: "text", md:
          "Passing accuracy also has to survive fatigue, and there are two kinds worth separating." },
        { kind: "science", title: "Mental fatigue, measured on the pass itself",
          body: "On the physical side, a review by Quílez Maimón that we’ll be discussing in the next section concluded that optimal physical conditioning is needed to maintain passing performance across a game. On the mental side, there is a direct measurement." },
        { kind: "science", title: "Mental fatigue, measured on the pass itself (continued)",
          body: "Zhou, Hou, Sang, Jiang, Hu and Li (2026, Frontiers in Psychology) put 27 trained university players through the same decision-embedded pass and shot sequence under low, moderate and high mental fatigue, each player doing all three. Nothing about the task changed between conditions. Every link in the chain degraded anyway.",
          items: [
            { term: "Passing accuracy", def: "87.8% at low fatigue, 84.1% at moderate, 79.3% at high" },
            { term: "Pass landing error", def: "22.6 cm, then 26.8 cm, then 33.4 cm. The passes that did arrive arrived further from where they were aimed" },
            { term: "Catch errors", def: "6.9%, then 9.8%, then 14.6%. The cost does not stay with the passer" },
            { term: "Catch to shot time", def: "842 ms, then 903 ms, then 992 ms. Fifteen hundredths of a second handed back to the defense on every catch" },
            { term: "Shooting", def: "54.7%, then 51.2%, then 46.1%. The end of the chain the pass was serving" },
          ] },
        { kind: "text", md:
          "That is this whole masterclass measured in one study. The target never moved, the players never got worse at passing, and accuracy fell anyway, because the conditions around the pass changed. It is also the clearest evidence for the standard in chapter 1: accuracy degraded first and most, and the catch and the shot degraded behind it." },
        { kind: "text", md:
          "Games ask players to process information continuously. Passing late in a possession, late in a game, or after repeatedly solving hard defensive problems is a different task from passing at the start of a clean drill." },
        { kind: "callout", tone: "warn", title: "What this does not mean",
          body: "It does not mean every passing drill has to exhaust players. It means that accuracy under fresh, predictable conditions does not automatically survive once the cognitive demand goes up, so do not take the clean rep as proof." },
      ],
    },
    // =================================================================
    {
      id: "putting-it-into-practice",
      title: "Putting it into practice",
      section: "Putting it into practice",
      divider: true,
      blocks: [
        { kind: "text", md:
          "Once you can see the variables, training becomes easier to organize." },
        { kind: "science", title: "Quílez Maimón, Courel-Ibáñez and Rojas Ruíz (2020)",
          body: "There’s actually a cool study on this, too. A systematic review of 37 basketball passing studies (Quílez Maimón, Courel-Ibáñez and Rojas Ruíz, 2020) organized the research behind passing accuracy in basketball. Four of its five conclusions are directly about the conditions a pass is made under:" },
        { kind: "text", md:
          "- that passing should be assessed under uncertain and variable conditions to say anything about competitive performance\n- that new and random activities help the learning transfer,\n- that practice should include overwhelming factors to reduce the effect of pressure and choking\n- small sided games and changing environments are the best training situations for improving passing\n- A fifth is about the body: optimal physical conditioning is needed to hold passing performance up across a game." },
        { kind: "text", md:
          "The takeaway is not that stationary passing work is useless. It builds the underlying ability to control where the ball goes, and you need that. The problem is mistaking that ability for the complete skill." },
        { kind: "text", md:
          "Anyways, here’s the full approach." },
      ],
    },
    // =================================================================
    {
      id: "pillar-1-general-accuracy",
      title: "Pillar 1: build general accuracy",
      section: "Putting it into practice",
      blocks: [
        { kind: "text", md:
          "There is still value in simple passing work. Players need an underlying ability to control where the ball goes and sharpening the wide variety of stationary movement patterns. Can they consistently put the ball exactly where they intend? Stationary work is useful here precisely because it strips out the other variables and lets them accumulate repetitions." },
        { kind: "text", md:
          "I’d also encourage you to consider more general qualities here, like general ball control and dexterity as you can see in the clip below:" },
        { kind: "video", muxPlaybackId: VIDEOS.basicOneHand,
          caption: "Basic One Hand Passing Warmup" },
        { kind: "text", md:
          "Progress by making it more dynamic and adding in a defensive challenge:" },
        { kind: "video", muxPlaybackId: VIDEOS.passingWarmup,
          caption: "Passing Warmup" },
        { kind: "video", muxPlaybackId: VIDEOS.simultaneous,
          caption: "Simultaneous Passing W Defense" },
        { kind: "heading", level: 3, text: "Expand the toolbox" },
        { kind: "text", md:
          "General accuracy shouldn’t mean repeating one type of pass. Players should be accurate from different release points and with different ball paths, and the point is that the two combine." },
        { kind: "accuracyGraphic", name: "toolbox", caption:
          "Release point and ball path are two separate choices. Train them as a grid and a player has twenty solutions. Train them as named passes and they have a handful of techniques." },
        { kind: "text", md:
          "The goal is not collecting techniques for their own sake. It is expanding the number of solutions available when the obvious one is taken away." },
      ],
    },
    // =================================================================
    {
      id: "pillar-2-release-windows",
      title: "Pillar 2: perceive release windows",
      section: "Putting it into practice",
      blocks: [
        { kind: "text", md:
          "Now add the defender on the passer. The player has two problems at once: find an available window, and still hit the intended target. This is very different from passing around a cone. They have to perceive when and where a release becomes available, then organize the pass fast enough to use it." },
        { kind: "video", muxPlaybackId: VIDEOS.throughPressure,
          caption: "Passing Through Pressure" },
      ],
    },
    // =================================================================
    {
      id: "pillar-3-receivers-defender",
      title: "Pillar 3: clear the receiver's defender",
      section: "Putting it into practice",
      blocks: [
        { kind: "text", md:
          "Now move the defender off the passer and onto the receiver. The player has to manipulate speed, height, angle, timing and ball path to get the pass safely around the defender while still putting it in the receiver's strike zone." },
        { kind: "text", md:
          "This is the pillar that teaches something coaches often split apart: avoiding the defender and delivering an accurate pass are not two problems. They have to be solved at the same time, with one throw." },
        { kind: "text", md:
          "Much of this can be implemented by adding an incentivized passer into small-sided games like the below and building the culture of making quality passes for their teammates:" },
        { kind: "video", muxPlaybackId: VIDEOS.addingPasser,
          caption: "Adding a Passer to Games" },
        { kind: "video", muxPlaybackId: VIDEOS.gapHelpRead,
          caption: "Gap Help Read" },
        { kind: "text", md:
          "Or by exaggerating the window for error (or lack thereof), like this:" },
        { kind: "video", muxPlaybackId: VIDEOS.contestedCatches,
          caption: "Contested Catches" },
      ],
    },
    // =================================================================
    {
      id: "pillar-4-overload",
      title: "Pillar 4: overload the system",
      section: "Putting it into practice",
      blocks: [
        { kind: "text", md:
          "Advanced passers need exposure to passes made late, from unusual body positions, through small windows, with minimal preparation, using unconventional releases, and after chaotic movement." },
        { kind: "text", md:
          "We use difficult passing problems because we want players to expand what they are physically capable of solving. If a player can hold accuracy when their body is disorganized, the information arrives late, the angle is unusual and the window is tiny, then the ordinary passes become much easier." },
        { kind: "video", muxPlaybackId: VIDEOS.dexterity,
          caption: "Passing & Handling Dexterity Warmup Series" },
        { kind: "video", muxPlaybackId: VIDEOS.oneHandOverload,
          caption: "One Hand Overload" },
        { kind: "video", muxPlaybackId: VIDEOS.threeSixty,
          caption: "360 Passing" },
      ],
    },
    // =================================================================
    {
      id: "pillar-5-into-shooting",
      title: "Pillar 5: build passing into shooting",
      section: "Putting it into practice",
      blocks: [
        { kind: "text", md:
          "Passing accuracy does not always need its own drill. The easiest way to add volume is to make it matter during shooting, because players are already passing to each other in those drills. Attach a standard and the repetitions start counting." },
        { kind: "actionSteps", items: [
          "A strike-zone pass is worth a point. A poor one is worth nothing.",
          "Give the passer a specific solution to use: one hand, off the bounce, from a wide release.",
          "Add a defender on the passer so the release window has to be found.",
          "Ask the shooter, not the passer, whether the pass was in the zone.",
        ]},
        { kind: "callout", tone: "tip", title: "The principle:",
          body: "If players are already passing during practice, they are already practicing passing. The only question is whether those repetitions are intentional." },
        { kind: "video", muxPlaybackId: VIDEOS.strikeZoneShoot,
          caption: "Strike Zone Dynamic Shooting" },
        { kind: "heading", level: 3, text: "Intentionally throw bad passes" },
        { kind: "text", md:
          "For more advanced players, accuracy can be developed by deliberately missing the normal target. Instead of always throwing the perfect strike-zone pass, give the passer a harder and more specific challenge: outside the left side, outside the right, high, low, to a specific hand, to a specific area outside the frame. The receiver knows the target beforehand." },
        { kind: "text", md:
          "That changes the question from \"can you throw an easy pass\" to \"can you place the ball exactly where you choose.\" It develops the receiver's ability to handle imperfect passes at the same time, and it works well as a competition: one player calls the target, the passer has to hit it." },
      ],
    },
    // =================================================================
    {
      id: "pillar-6-live-play",
      title: "Pillar 6: incentivize accuracy in live play",
      section: "Putting it into practice",
      blocks: [
        { kind: "text", md:
          "Eventually accuracy has to survive the game. Constraints and scoring can direct attention toward passing quality without you having to say anything." },
        { kind: "actionSteps", items: [
          "A strike-zone assist is worth extra points.",
          "After a set number of clearly poor-location passes, the possession changes hands.",
          "For a stretch of play, only bounce passes, or a required solution, or a rewarded pass type.",
        ]},
        { kind: "text", md:
          "**This, by far, is likely the most bang for your buck you’ll get. We’ve seen huge results by simply playing more basketball, incentivizing good passes, building a culture around it, and it makes sense. Players get a ton of highly representative reps, with their attention turned towards making good passes.**" },
        { kind: "text", md:
          "Coaches often bang their head against the wall trying to develop more accurate passers, and avoid playing live basketball in practice partially because passes are inaccurate and it looks “messy.” In reality, this messiness is exactly what they need." },
        { kind: "text", md:
          "You can do this in a number of ways, from unstructured play/scrimmages, to snapshot small-sided games like this push-pull 2v2, to more engineered drills like you’ll see below." },
        { kind: "video", muxPlaybackId: VIDEOS.pushPull,
          caption: "2v2 Push Pull" },
        { kind: "video", muxPlaybackId: VIDEOS.bounceConstraint,
          caption: "Bounce Pass Constraint" },
        { kind: "video", muxPlaybackId: VIDEOS.pgKeys,
          caption: "PG Keys: Passing Development" },
      ],
    },
    // =================================================================
    {
      id: "bigger-idea",
      title: "The bigger idea",
      section: "Closing",
      blocks: [
        { kind: "heading", level: 3, text: "Accuracy without removing adaptability" },
        { kind: "text", md:
          "There is one important final distinction. We want passers who make the receiver's job easier. We do not want receivers who can only perform when every pass is perfect. Those are two separate development goals and they are easy to collapse into one." },
        { kind: "compare",
          leftTitle: "For the passer",
          leftItems: [
            "Raise the standard",
            "Put the ball exactly where the teammate needs it",
            "Judge the arrival, not the catch",
            "Expand the solutions available when the obvious one is gone",
          ],
          rightTitle: "For the receiver",
          rightItems: [
            "Expand adaptability",
            "Catch and perform from imperfect passes",
            "Handle high, low, wide and behind",
            "Keep the next action alive when the pass did not help",
          ],
        },
        { kind: "text", md:
          "These are not contradictory. A good offense has passers who create easy catches and receivers who can solve difficult ones." },
        { kind: "heading", level: 3, text: "The bigger idea" },
        { kind: "text", md:
          "Passing accuracy is not simply a technical skill. It emerges from the interaction of perception, timing, ball control, movement, dexterity, decision-making, game understanding, defender positioning, receiver movement, fatigue, and the passing solutions the player can actually reach." },
        { kind: "text", md:
          "That is why a player can look elite in a stationary drill and still struggle to deliver accurate passes in games. The drill tested one of those things." },
      ],
    },
    // =================================================================
    {
      id: "research",
      title: "Research note",
      section: "Closing",
      blocks: [
        { kind: "text", md:
          "Three sources are cited in this masterclass, and one commonly repeated claim is deliberately not." },
        { kind: "science", title: "Quílez Maimón, Courel-Ibáñez and Rojas Ruíz (2020)",
          body: "The Basketball Pass: A Systematic Review. Journal of Human Kinetics, 71, 275 to 284. A review of 37 studies across performance analysis, motor control, biomechanics, physical conditioning and mental factors. Its conclusions include that passing should be assessed under uncertain and variable conditions, that new and random activities help learning transfer to competition, that practice should include overwhelming factors to reduce the effect of pressure and choking, that optimal physical conditioning is needed to maintain passing performance through a game, and that small sided games and changing environments are the best training situations for improving passing." },
        { kind: "science", title: "Zhou, Hou, Sang, Jiang, Hu and Li (2026)",
          body: "Decision-making performance and motor skill consistency in basketball short pass to shot sequences under different levels of mental fatigue: an integrated psychophysiological dynamics analysis. Frontiers in Psychology, DOI 10.3389/fpsyg.2026.1831767, published 3 June 2026. A within-subject crossover with 27 trained male university players, each completing the same decision-embedded pass and shot sequence under low, moderate and high mental fatigue. Decision accuracy, reaction time, sequence success rate and motor consistency all degraded as fatigue rose, alongside increased electrodermal activity and reduced heart rate variability. This is the source for the fatigue numbers in chapter 6, and it is the one study here that measures passing accuracy and pass placement directly rather than inferring them from turnovers." },
        { kind: "science", title: "Cao, Soh, Roslan, Sun, Soh and Qian (2022)",
          body: "Mental Fatigue and Basketball Performance: A Systematic Review. Frontiers in Psychology, 12, 819081. Found that mental fatigue impairs technical performance, including free throws, three point shooting and total turnovers, and impairs cognitive performance including decision-making. It reaches the same conclusion as Zhou from a different direction, across a body of studies rather than one controlled sequence, which is why both are here. On its own it is evidence about turnovers and decisions rather than passing accuracy itself, so it supports the fatigue section rather than carrying it." },
        { kind: "callout", tone: "warn", title: "The claim we are not making",
          body: "You will see large shooting-percentage differences quoted for in-zone against out-of-zone passes. We could not verify those figures against a published dataset, so they are not in this masterclass. Note that the shooting numbers in chapter 6 are a different claim: those compare fatigue levels within one controlled sequence, not pass placement across games. The in-zone coaching principle does not depend on a number." },
      ],
    },
  ],
};

export default PASSING_ACCURACY_MODULE;
